import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import AdminShell from './layouts/AdminShell';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ToolsListPage from './pages/ToolsListPage';
import ToolEditPage from './pages/ToolEditPage';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import MessagesPage from './pages/MessagesPage';
import MessageEditPage from './pages/MessageEditPage';
import AgentToolsPage from './pages/AgentToolsPage';
import QualityCenterLayout from './pages/quality/QualityCenterLayout';
import DesktopAgentPage from './pages/DesktopAgentPage';
import QualityOverviewPage from './pages/quality/QualityOverviewPage';
import QualityIssueWorkbenchPage from './pages/quality/QualityIssueWorkbenchPage';
import QualityReportsPage from './pages/quality/QualityReportsPage';
import QualityEventsPage from './pages/quality/QualityEventsPage';
import QualityRemediationPage from './pages/quality/QualityRemediationPage';
import AuditLogPage from './pages/AuditLogPage';
import JobsPage from './pages/JobsPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import NotFoundPage from './pages/NotFoundPage';
import './styles/quality.scss';
import './styles/desktop-agent.scss';
import './styles/desktop-agent-pairing.scss';

const FullScreenSpinner: React.FC<{ label?: string }> = ({ label = 'Caricamento…' }) => (
    <div className="d-flex flex-column align-items-center justify-content-center vh-100">
        <div className="spinner-border text-primary" role="status" />
        <div className="mt-3 small text-body-secondary">{label}</div>
    </div>
);

const App: React.FC = () => {
    const { status } = useAuth();

    if (status === 'loading') return <FullScreenSpinner label="Verifica autorizzazioni…" />;
    if (status === 'error')
        return (
            <div className="login-shell">
                <div className="card shadow-lg" style={{ maxWidth: 480 }}>
                    <div className="card-body text-center">
                        <i className="bi bi-exclamation-octagon text-danger fs-1" />
                        <h4 className="mt-2">Errore</h4>
                        <p className="text-body-secondary">
                            Impossibile verificare il ruolo. Ricarica la pagina o
                            contatta il supporto.
                        </p>
                    </div>
                </div>
            </div>
        );

    // Utente non loggato → solo /login. Tutto il resto va a /login.
    if (status === 'anonymous')
        return (
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );

    // Loggato ma non admin → schermata access denied.
    if (status === 'denied') return <AccessDeniedPage />;

    // Da qui in poi: admin verificato. Routes "shell" con sidebar + content.
    return (
        <Routes>
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
            <Route element={<AdminShell />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/tools" element={<ToolsListPage />} />
                <Route path="/tools/:id" element={<ToolEditPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/users/:id" element={<UserDetailPage />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/messages/new" element={<MessageEditPage />} />
                <Route path="/messages/:id" element={<MessageEditPage />} />
                <Route path="/quality" element={<QualityCenterLayout />}>
                    <Route index element={<QualityOverviewPage />} />
                    <Route path="issues" element={<QualityIssueWorkbenchPage />} />
                    <Route path="reports" element={<QualityReportsPage />} />
                    <Route path="events" element={<QualityEventsPage />} />
                    <Route path="remediation" element={<QualityRemediationPage />} />
                </Route>
                <Route path="/reports" element={<Navigate to="/quality/reports" replace />} />
                <Route path="/diagnostics" element={<Navigate to="/quality/events" replace />} />
                <Route path="/agents" element={<AgentToolsPage />} />
                <Route path="/desktop-agent" element={<DesktopAgentPage />} />
                <Route path="/audit" element={<AuditLogPage />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
    );
};

export default App;
